import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Briefcase, Plus, Pencil, Trash2, Users, Sparkles, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = ["full_time", "part_time", "contract", "casual", "seasonal", "internship"];
const STATUSES = ["new", "screening", "interview", "offer", "rejected", "hired", "withdrew"];

const STATUS_COLOR = {
  new: "bg-blue-100 text-blue-700 border-blue-300",
  screening: "bg-amber-100 text-amber-700 border-amber-300",
  interview: "bg-purple-100 text-purple-700 border-purple-300",
  offer: "bg-emerald-100 text-emerald-700 border-emerald-300",
  hired: "bg-emerald-200 text-emerald-800",
  rejected: "bg-muted text-muted-foreground",
  withdrew: "bg-muted text-muted-foreground",
};

const blankPost = () => ({
  title: "", description: "", location: "", employment_type: "full_time",
  salary_min: null, salary_max: null, salary_currency: "CAD",
  requirements: [], benefits: "", closing_at: "", is_active: true, notes: "",
});

const blankApp = (pid) => ({
  posting_id: pid, candidate_name: "", candidate_email: "", candidate_phone: "",
  resume_url: "", cover_letter: "", status: "new",
});

export default function Recruitment() {
  const [tab, setTab] = useState("postings");
  const [postings, setPostings] = useState([]);
  const [apps, setApps] = useState([]);
  const [summary, setSummary] = useState(null);
  const [activePosting, setActivePosting] = useState(null);
  const [postDialog, setPostDialog] = useState(null);
  const [appDialog, setAppDialog] = useState(null);
  const [aiResult, setAiResult] = useState(null);

  const load = async () => {
    try {
      const [p, a, s] = await Promise.all([
        axios.get(`${API}/job-postings?include_inactive=true`),
        axios.get(`${API}/applications`),
        axios.get(`${API}/recruitment/summary`),
      ]);
      setPostings(p.data || []);
      setApps(a.data || []);
      setSummary(s.data);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const savePost = async () => {
    if (!postDialog.title) { toast.error("Title required"); return; }
    try {
      if (postDialog.id) await axios.put(`${API}/job-postings/${postDialog.id}`, postDialog);
      else await axios.post(`${API}/job-postings`, postDialog);
      toast.success("Posting saved"); setPostDialog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const deletePost = async (id) => {
    if (!window.confirm("Deactivate?")) return;
    try { await axios.delete(`${API}/job-postings/${id}`); load(); } catch { toast.error("Failed"); }
  };

  const saveApp = async () => {
    try {
      await axios.post(`${API}/applications`, appDialog);
      toast.success("Application added"); setAppDialog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const setStatus = async (appId, status) => {
    try { await axios.put(`${API}/applications/${appId}/status`, { status }); load(); }
    catch { toast.error("Status update failed"); }
  };

  const runAi = async (appId) => {
    setAiResult({ loading: true });
    try {
      const r = await axios.post(`${API}/applications/ai-match`, { application_id: appId });
      setAiResult(r.data); load();
    } catch (e) {
      setAiResult(null);
      toast.error(e?.response?.data?.detail || "AI match failed");
    }
  };

  const appsForActive = activePosting ? apps.filter((a) => a.posting_id === activePosting.id) : apps;

  return (
    <div className="space-y-6 animate-in" data-testid="recruitment-page">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-lg bg-primary/10"><Briefcase className="h-7 w-7 text-primary" /></div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Recruitment</h1>
          <p className="text-muted-foreground">Job postings + applications + AI screening.</p>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="summary-cards">
          <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Active postings</p><p className="text-2xl font-bold">{summary.active_postings}</p></CardContent></Card>
          <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Total applications</p><p className="text-2xl font-bold">{summary.total_applications}</p></CardContent></Card>
          <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">New</p><p className="text-2xl font-bold text-blue-700">{summary.new_count}</p></CardContent></Card>
          <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">In interview</p><p className="text-2xl font-bold text-purple-700">{summary.by_status?.interview || 0}</p></CardContent></Card>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="postings" data-testid="tab-postings"><Briefcase className="h-4 w-4 mr-1" />Postings</TabsTrigger>
          <TabsTrigger value="applications" data-testid="tab-applications"><Users className="h-4 w-4 mr-1" />Applications</TabsTrigger>
        </TabsList>

        <TabsContent value="postings">
          <Card>
            <CardHeader className="flex flex-row justify-between items-center">
              <div><CardTitle>{postings.length} postings</CardTitle><CardDescription>Click a row to filter applications below.</CardDescription></div>
              <Button onClick={() => setPostDialog(blankPost())} data-testid="new-posting-btn"><Plus className="h-4 w-4 mr-1" /> New posting</Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Title</TableHead><TableHead>Type</TableHead><TableHead>Salary</TableHead><TableHead>Apps</TableHead><TableHead>Status</TableHead><TableHead className="text-right" />
                </TableRow></TableHeader>
                <TableBody>
                  {postings.map((p) => (
                    <TableRow key={p.id} className={`cursor-pointer ${activePosting?.id === p.id ? "bg-primary/5" : ""} ${!p.is_active ? "opacity-50" : ""}`}
                      onClick={() => { setActivePosting(p); setTab("applications"); }} data-testid={`posting-row-${p.id}`}>
                      <TableCell className="font-medium">{p.title}</TableCell>
                      <TableCell><Badge variant="outline">{p.employment_type}</Badge></TableCell>
                      <TableCell className="text-xs">{p.salary_min ? `$${p.salary_min}-${p.salary_max || "?"}` : "—"}</TableCell>
                      <TableCell>{p.application_count}</TableCell>
                      <TableCell>{p.is_active ? <Badge>Active</Badge> : <Badge variant="outline">Inactive</Badge>}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setPostDialog({ ...p }); }}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={(e) => { e.stopPropagation(); deletePost(p.id); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {postings.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No postings yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="applications">
          <Card>
            <CardHeader className="flex flex-row justify-between items-center">
              <div>
                <CardTitle>{appsForActive.length} application{appsForActive.length === 1 ? "" : "s"} {activePosting && `· ${activePosting.title}`}</CardTitle>
                <CardDescription>{activePosting ? <button className="text-primary hover:underline" onClick={() => setActivePosting(null)}>Clear filter</button> : "All postings"}</CardDescription>
              </div>
              {activePosting && <Button onClick={() => setAppDialog(blankApp(activePosting.id))} data-testid="new-app-btn"><Plus className="h-4 w-4 mr-1" /> Add applicant</Button>}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Name</TableHead><TableHead>Status</TableHead><TableHead>AI Match</TableHead><TableHead>Submitted</TableHead><TableHead className="text-right" />
                </TableRow></TableHeader>
                <TableBody>
                  {appsForActive.map((a) => (
                    <TableRow key={a.id} data-testid={`app-row-${a.id}`}>
                      <TableCell>
                        <p className="font-medium">{a.candidate_name}</p>
                        <p className="text-xs text-muted-foreground">{a.candidate_email}</p>
                      </TableCell>
                      <TableCell>
                        <Select value={a.status} onValueChange={(v) => setStatus(a.id, v)}>
                          <SelectTrigger className="w-32 h-7" data-testid={`status-select-${a.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {a.ai_match_score !== null && a.ai_match_score !== undefined ? <Badge className={STATUS_COLOR.offer} variant="outline">{a.ai_match_score}/100</Badge> : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-xs">{a.submitted_at && new Date(a.submitted_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => runAi(a.id)} data-testid={`ai-match-${a.id}`}><Sparkles className="h-3.5 w-3.5 text-primary" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {appsForActive.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No applications.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Posting editor */}
      <Dialog open={!!postDialog} onOpenChange={(o) => !o && setPostDialog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{postDialog?.id ? "Edit posting" : "New posting"}</DialogTitle></DialogHeader>
          {postDialog && (
            <div className="space-y-3">
              <div><Label>Title</Label><Input value={postDialog.title} onChange={(e) => setPostDialog({ ...postDialog, title: e.target.value })} data-testid="post-title-input" /></div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label>Location</Label><Input value={postDialog.location || ""} onChange={(e) => setPostDialog({ ...postDialog, location: e.target.value })} /></div>
                <div>
                  <Label>Type</Label>
                  <Select value={postDialog.employment_type} onValueChange={(v) => setPostDialog({ ...postDialog, employment_type: v })}>
                    <SelectTrigger data-testid="post-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Closes</Label><Input type="date" value={postDialog.closing_at || ""} onChange={(e) => setPostDialog({ ...postDialog, closing_at: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Salary min</Label><Input type="number" value={postDialog.salary_min ?? ""} onChange={(e) => setPostDialog({ ...postDialog, salary_min: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid="salary-min-input" /></div>
                <div><Label>Salary max</Label><Input type="number" value={postDialog.salary_max ?? ""} onChange={(e) => setPostDialog({ ...postDialog, salary_max: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid="salary-max-input" /></div>
              </div>
              <div><Label>Description</Label><Textarea rows={4} value={postDialog.description || ""} onChange={(e) => setPostDialog({ ...postDialog, description: e.target.value })} data-testid="post-desc-input" /></div>
              <div><Label>Requirements (raw JSON: [{"{\"label\":\"x\",\"must_have\":true}"}])</Label>
                <Textarea rows={3} className="font-mono text-xs" value={JSON.stringify(postDialog.requirements || [], null, 2)}
                  onChange={(e) => { try { setPostDialog({ ...postDialog, requirements: JSON.parse(e.target.value) }); } catch {} }} /></div>
              <div><Label>Benefits</Label><Textarea rows={2} value={postDialog.benefits || ""} onChange={(e) => setPostDialog({ ...postDialog, benefits: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPostDialog(null)}>Cancel</Button>
            <Button onClick={savePost} data-testid="save-posting-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* App add dialog */}
      <Dialog open={!!appDialog} onOpenChange={(o) => !o && setAppDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add applicant</DialogTitle></DialogHeader>
          {appDialog && (
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={appDialog.candidate_name} onChange={(e) => setAppDialog({ ...appDialog, candidate_name: e.target.value })} data-testid="cand-name-input" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Email</Label><Input value={appDialog.candidate_email} onChange={(e) => setAppDialog({ ...appDialog, candidate_email: e.target.value })} /></div>
                <div><Label>Phone</Label><Input value={appDialog.candidate_phone} onChange={(e) => setAppDialog({ ...appDialog, candidate_phone: e.target.value })} /></div>
              </div>
              <div><Label>Resume URL</Label><Input value={appDialog.resume_url} onChange={(e) => setAppDialog({ ...appDialog, resume_url: e.target.value })} /></div>
              <div><Label>Cover letter</Label><Textarea rows={4} value={appDialog.cover_letter} onChange={(e) => setAppDialog({ ...appDialog, cover_letter: e.target.value })} data-testid="cover-letter-input" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAppDialog(null)}>Cancel</Button>
            <Button onClick={saveApp} data-testid="save-app-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI result */}
      <Dialog open={!!aiResult} onOpenChange={(o) => !o && setAiResult(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>AI screening</DialogTitle></DialogHeader>
          {aiResult?.loading && <p className="text-muted-foreground">Analyzing with Claude Sonnet…</p>}
          {aiResult?.ai_response && (
            <div className="space-y-3" data-testid="ai-result">
              <div className="text-center"><p className="text-xs text-muted-foreground">Match score</p><p className="text-4xl font-bold text-primary">{aiResult.ai_response.match_score}/100</p></div>
              <p className="text-sm">{aiResult.ai_response.summary}</p>
              {aiResult.ai_response.strengths && (
                <div><p className="text-sm font-medium">Strengths</p>
                  <ul className="text-sm list-disc list-inside">{aiResult.ai_response.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul></div>
              )}
              {aiResult.ai_response.gaps && (
                <div><p className="text-sm font-medium text-amber-700">Gaps</p>
                  <ul className="text-sm list-disc list-inside">{aiResult.ai_response.gaps.map((s, i) => <li key={i}>{s}</li>)}</ul></div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
